import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { Button } from "react-bootstrap";

import { Property } from "components/Editor/components/properties";
import { BRANDKIT_COLOR_COMPONENT } from "constants/brandKit/color";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import EmptyMessage from "../../components/EmptyMessage";
import DeleteModal from "../../components/DeleteModal";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";

import { 
  createBrandkitColor, 
  deleteBrandkitColors, 
  deleteBrandkitNewColors, 
  fetchBrandkitColors, 
  invalidateBrandkitColors, 
  renameBrandkitColors, 
  updateBrandkitColorProperty, 
  updateBrandkitColors 
} from "actions/brandKit/colors";

import styles from '../brandkit.elements.module.scss';
import useToastMessage from "../../components/toastMessageHook";
import { MIXPANEL_BRANDKIT_CREATED } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";

function ColoursElement() {
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const [toggleBrandElements, setToggleBrandElements] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([])
  const [isHovered, setIsHovered] = useState(false);

  const paletteIds = useSelector((state) => _.get(state, "brandKit.activated.items", []));
  const componentsList = useSelector(state => _.get(state, 'brandKit.colors.components')) || [];
  const loading = useSelector(state => _.get(state, 'brandKit.colors.loading', false));
  const deleting = useSelector(state => _.get(state, 'brandKit.colors.deleting', false));
  const updating = useSelector(state => _.get(state, 'brandKit.colors.updating', false));
  const updated = useSelector(state => _.get(state, 'brandKit.colors.updated', false));
  const error = useSelector(state => _.get(state, 'brandKit.colors.error', false));


  const paletteId = useMemo(
    () => _.find(paletteIds, { type: "COLORS" })?.id,
    [paletteIds]
  );

  const enabled = useMemo(
    () => _.find(paletteIds, { type: "COLORS" })?.enabled,
    [paletteIds]
  );

  useEffect(()=>{
    if (enabled != toggleBrandElements) {
      setToggleBrandElements(enabled)
    }
  },[enabled])


  useEffect(() => { 
    if(paletteId){
      dispatch(fetchBrandkitColors(paletteId));
    }
  }, [paletteId])

  useEffect(() => {
    if (!loading && showDeleteModal && !deleting) {
      setShowDeleteModal(false);
      setSelectedItems([]);
    }
  }, [deleting]);

  useEffect(() => {
    setSelectedItems([]);
  },[updating])

  useToastMessage(updated, invalidateBrandkitColors, error, 'Colors');

  const handleCreateColor = () => {
    dispatch(createBrandkitColor(BRANDKIT_COLOR_COMPONENT()));
    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }, 300);
  };

  const deleteModalOpenHandler = () => {
    if (selectedItems.length === 0) return;
    setShowDeleteModal(true);
  };

  const resetHandler = () => {
    if(paletteId){
      dispatch(fetchBrandkitColors(paletteId));
    }
  };

  const handleDelete = () => {
    const ids = selectedItems
    .filter(item => !item.newComponent)
    .map(item => item.id);

    if (ids.length > 0) {
      dispatch(deleteBrandkitColors(ids, paletteId))
    }

    const newComponentsIds = selectedItems
    .filter(item => item.newComponent)
    .map(item => item.id);

    if (newComponentsIds.length > 0) {
      dispatch(deleteBrandkitNewColors(newComponentsIds))
      if (!deleting) {
        setTimeout(() => {
          setShowDeleteModal(false);
          setSelectedItems([]);
        }, 500);
      }
    }
  };

  const saveChangesHandler = () => {
    const request = componentsList
      ?.filter(({ changes }) => changes)
      .map(({ id, name, newComponent, draft, order }) => ({
        name,
        ...(newComponent ? {} : { id }),
        order,
        colors: draft?.content || [],
      }));
  
    dispatch(updateBrandkitColors(paletteId, request));

    var mixpanelPayload = {
      brandkit_element_type: "Colors"
    }

    dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_CREATED, mixpanelPayload));
  };

  return (
    <div className={styles.wrapper}>
      <div className={cx(styles.secondaryContainer, styles.container)}>
        <div className={styles.mainContainer}>
          <Header
            selectedCategory={"Colours"}
            selectedItems={selectedItems}
            toggleBrandElements={toggleBrandElements}
            setToggleBrandElements={setToggleBrandElements}
            deleteHandler={deleteModalOpenHandler}
            loading={loading}
            paletteId={paletteId}
          >
            <Button 
              variant="primary" 
              id = 'newColorPalette' 
              className={styles.uploadButton} 
              style={{backgroundColor: isHovered ? "var(--primaryAppColor)" : "transparent"}}
              onClick={handleCreateColor}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <i className="cleverstory-plus" />
              {"New Palette"}
            </Button>
          </Header>

          <div className={styles.mainContentWrapper} ref={containerRef}>
            <div>
            {(loading && componentsList?.length === 0 )? (
                <div className={styles.loaderContainer}>
                  <label>Just a moon-ute... Scanning for colours... </label>
                </div>
              ) : (!loading && componentsList?.length === 0) ? (
                <EmptyMessage />
              ) : (
                componentsList?.map((data) => (
                  <ColourComp
                    key={data.id}
                    paletteId={paletteId}
                    component={data}
                    deleting={deleting}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    showDeleteModal={showDeleteModal}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          showDeleteModal={true}
          message={`Are you sure you want to delete ${
            selectedItems.length > 1
              ? `these ${selectedItems.length} files`
              : "this file"
          }?`}
          title={`Delete Colours`}
          isDeleting={deleting}
          backdrop={deleting}
          cancelHandler={() => setShowDeleteModal(false)}
          handleDeleteClick={handleDelete}
        />
      )}

      <Footer
        resetHandler={resetHandler}
        updating={updating}
        saveChangesHandler={saveChangesHandler}
        saveDisable={updating || componentsList?.length === 0}
      />
    </div>
  );
}

function ColourComp({ deleting, paletteId, component, selectedItems, setSelectedItems }) {
  const { id, name, draft } = component || {};

  const dispatch = useDispatch();

  const multiple = true;
  const [show, setShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showColour, setShowColour] = useState(false);
  const [renameEnabled, setRenameEnabled] = useState(false);
  const [headerName, setHeaderName] = useState(name);

  const colorTargetRef = useRef(null);
  const colorTargetAddRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const input = inputRef?.current;
    if (!input) return;
    input.value = name;
    setHeaderName(name);
  }, [name]);

  useEffect(() => {
    let input = inputRef?.current;
    if (!input) return;
    if (renameEnabled) {
      input.focus();
    }

    const blurHandler = () => {
      setRenameEnabled(false);
      renameHandler(id, input.value);
      setHeaderName(input.value);
    };

    const keyHandler = (event) => {
      if (event.key === "Enter") {
        blurHandler();
      }
    };

    input?.addEventListener("blur", blurHandler);
    input?.addEventListener("keydown", keyHandler);
    return () => {
      input?.removeEventListener("blur", blurHandler);
      input?.removeEventListener("keydown", keyHandler);
    };
  }, [renameEnabled]);

  useEffect(() => {
    if(showColour) {
      const request = [
        ...draft?.content,
        { r: 0, g: 0, b: 0, a: 1, type: "solid" }
      ]
      dispatch(updateBrandkitColorProperty(id, request));
    }
  },[showColour])

  const handleAddColor = (data) => {
    if (data.type === "solid" && data.r === 0 && data.g === 0 && data.b === 0 && data.a === 1) {
      return;
    }
    let request;
    let existingColors = draft?.content ? [...draft.content] : [];
    const color = data.type === "solid" 
    ? ((data.r || data.g || data.b) 
        ? data 
        : { r: 0, g: 0, b: 0, a: 1, type: "solid" }) 
    : data;
    if (!showColour) {
      request = [...existingColors, color];
    } else {
      existingColors[existingColors.length - 1] = color;
      request = existingColors;
    }
  
    dispatch(updateBrandkitColorProperty(id, request));
  };

  const handleRemoveColor = (index) => {
    const colors = [...draft.content];
    colors.splice(index, 1); 

    const request = colors;

    dispatch(updateBrandkitColorProperty(id, request))
  };

  const handleSelect = (component) => {
    var updated = [...selectedItems];

    if (multiple) {
      const exists = updated.find(m => m.id === component.id) != null;
      if (exists) {
        updated = updated.filter(m => m.id !== component.id);
      } else {
        updated = [...updated, component];
      }
    } else {
      updated = [component];
    }
    setSelectedItems(updated)
  }

  const handleDelete = () => {

    if (component.newComponent) {
      dispatch(deleteBrandkitNewColors([id]))

      if (!deleting) {
        setTimeout(() => {
          setShowDeleteModal(false);
          setSelectedItems([]);
        }, 500);
      }
    } else {
      dispatch(deleteBrandkitColors([id], paletteId))
    }
  };

  const renameHandler = (id, name) => {
    dispatch(renameBrandkitColors(id, name));
  };

  return (
    <div
      className={cx(
        styles.componentStyleWrapper,
        (selectedItems?.some(data => data.id === id)) && styles.componentStyleWrapperSelected
      )}
      style={{ marginBottom: "20px" }}
      onClick={() => handleSelect(component)}
    >
      <div className={styles.newStyleHeaderWrapper}>
      <div>
        {!renameEnabled ?
          <TruncatedTextWithTooltip             
            content={headerName} 
            maxWidth={200}
          />
          :
          <input
            ref={inputRef}
            value={headerName}
            onChange={(e) => setHeaderName(e.target.value)}
            className={styles.widgetHeadingName}
            disabled={!renameEnabled}
          />
        }
      </div>
        <div className={styles.moreOptionsWrapper}>
          <Button
            variant="secondary"
            className={styles.btnIcons}
            onClick={(e) => {
              setShow(!show);
              e.stopPropagation();
            }}
          >
            <i className={"cleverstory-more"} />
          </Button>
          {show && (
            <div
              className={styles.moreOptionsList}
              onClick={(e) => e.stopPropagation()}
            >
              <span
                onClick={() => {
                  setRenameEnabled(!renameEnabled);
                  setShow(false);
                  inputRef?.current?.focus();
                }}
              >
                Rename
              </span>
              <span
                onClick={() => {
                  setShow(false);
                  setShowDeleteModal(true);
                }}
              >
                Delete
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.colorBoxContainer}>
        {draft?.content?.map((data, index) => (
          <span ref={colorTargetRef} key={index}>
          <ColourList
            data={data}
            removeColors={() => handleRemoveColor(index)}
          />
          </span>
        ))}
        <div
          className={styles.newColorWrapper}
          onClick={(e) => e.stopPropagation()}
          ref={colorTargetAddRef}
        >
          <Button
            variant="secondary"
            className={styles.plusWrapper}
            onClick={() => setShowColour(true)}
          >
            <i style={{ fontSize: "22px" }} className="cleverstory-plus" />
          </Button>
          <span>Add New</span>
        </div>
        {showColour && (
            <span>
              <Property.ColorPickerDialog
                target={colorTargetRef?.current ? colorTargetRef.current : colorTargetAddRef?.current}
                colorConfig={{
                  r: 0,
                  g: 0,
                  b: 0,
                  a: 1,
                  type: "solid",
                }}
                gradient={false}
                value={{
                  r: 0,
                  g: 0,
                  b: 0,
                  a: 1,
                  type: "solid",
                }}
                onChange={handleAddColor}
                onCancel={() => setShowColour(false)}
                brandkit={true}
                brandkitColorPosition={true}
              />
            </span>
          )}
      </div>

      {showDeleteModal && (
        <DeleteModal
          showDeleteModal={showDeleteModal}
          message={`Are you sure you want to delete ${headerName}?`}
          title={`Delete ${headerName}`}
          isDeleting={deleting}
          backdrop={deleting}
          cancelHandler={() => setShowDeleteModal(false)}
          handleDeleteClick={handleDelete}
        />
      )}
    </div>
  );
}

const ColourList = ({ data, removeColors }) => {
  const colorCategory = (data) => {
    if (data.type === "solid") {
      return `rgba(${data.r},${data.g},${data.b},${data.a})`;
    }
    const col = data.gradientColors?.[0];
    const col2 = data.gradientColors?.[1];
    return `linear-gradient(${data.degree}deg, rgba(${col.r},${col.g},${col.b},${col.a}) ${col.position}%, rgba(${col2.r},${col2.g},${col2.b},${col2.a}) ${col2.position}%)`;
  };

  const rgbaToHex = (r, g, b, a) => {
    const red = r.toString(16).padStart(2, "0");
    const green = g.toString(16).padStart(2, "0");
    const blue = b.toString(16).padStart(2, "0");

    const alpha = Math.round(a * 255)
      .toString(16)
      .padStart(2, "0");

    return `#${red}${green}${blue}${alpha}`;
  };

  return (
    <div className={styles.colorBoxWrapper}>
      <div
        className={styles.colorBox}
        style={{
          background: colorCategory(data),
        }}
      >
        <span
          className={styles.closeIcons}
          onClick={(e) => {
            e.stopPropagation();
            removeColors();
          }}
        >
          <i className={"cleverstory-close"} />
        </span>
      </div>
      <p>
        {data.type === "solid"
          ? rgbaToHex(data.r, data.g, data.b, data.a)
          : data?.gradientType}
      </p>
    </div>
  );
};

export default ColoursElement;